import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Users, Check, X, Clock } from "lucide-react";
import { api } from "@/lib/api";

interface PendingInvitation {
  id: string;
  team_id: string;
  team_name: string;
  inviter_name: string;
  inviter_email: string;
  role: string;
  created_at: string;
  expires_at: string;
  token: string;
}

export default function TeamInvitationPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [currentInvitation, setCurrentInvitation] = useState<PendingInvitation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Check for pending invitations on component mount
    checkPendingInvitations();
  }, []);

  const checkPendingInvitations = async () => {
    try {
      console.log('Checking for pending invitations...');
      const response = await api.get('/api/teams/invitations/pending');
      console.log('Pending invitations response:', response.data);
      
      if (response.data.invitations && response.data.invitations.length > 0) {
        console.log(`Found ${response.data.invitations.length} pending invitations`);
        setInvitations(response.data.invitations);
        setCurrentInvitation(response.data.invitations[0]);
        setIsOpen(true);
      } else {
        console.log('No pending invitations found');
      }
    } catch (error) {
      console.error('Error checking pending invitations:', error);
    }
  };

  const handleAccept = async () => {
    if (!currentInvitation) return;
    
    setIsLoading(true);
    try {
      await api.post(`/api/teams/invitations/accept/${currentInvitation.token}`);
      toast({
        title: "Success",
        description: `Successfully joined ${currentInvitation.team_name}!`,
      });
      
      // Remove the accepted invitation from the list
      const remainingInvitations = invitations.filter(inv => inv.id !== currentInvitation.id);
      setInvitations(remainingInvitations);
      
      // Show next invitation or close
      if (remainingInvitations.length > 0) {
        setCurrentInvitation(remainingInvitations[0]);
      } else {
        setIsOpen(false);
        // Reload the page to refresh team data
        window.location.reload();
      }
    } catch (error: any) {
      // Handle 402 Payment Required specifically
      if (error.response?.status === 402) {
        toast({
          title: "No Available Seats",
          description: error.response?.data?.detail || 'All assistant seats are occupied. The team owner needs to purchase more seats.',
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.response?.data?.detail || 'Failed to accept invitation',
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!currentInvitation) return;
    
    setIsLoading(true);
    try {
      await api.post(`/api/teams/invitations/decline/${currentInvitation.token}`);
      toast({
        title: "Info",
        description: "Invitation declined",
      });
      
      // Remove the declined invitation from the list
      const remainingInvitations = invitations.filter(inv => inv.id !== currentInvitation.id);
      setInvitations(remainingInvitations);
      
      // Show next invitation or close
      if (remainingInvitations.length > 0) {
        setCurrentInvitation(remainingInvitations[0]);
      } else {
        setIsOpen(false);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || 'Failed to decline invitation',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!currentInvitation) return null;

  const expiryDate = new Date(currentInvitation.expires_at);
  const isExpired = expiryDate < new Date();

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-primary/10 rounded-full">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div>
              <DialogTitle>Team Invitation</DialogTitle>
              <DialogDescription>
                You have {invitations.length} pending team invitation{invitations.length > 1 ? 's' : ''}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {isExpired ? (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 text-red-600">
                  <Clock className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Invitation Expired</p>
                    <p className="text-sm">This invitation has expired. Please ask for a new one.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardContent className="pt-6 space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Team</p>
                    <p className="font-medium text-lg">{currentInvitation.team_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Invited by</p>
                    <p className="font-medium">
                      {currentInvitation.inviter_name} ({currentInvitation.inviter_email})
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Your role</p>
                    <p className="font-medium capitalize">{currentInvitation.role}</p>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-2">
                <p className="text-sm font-medium">By joining, you'll get access to:</p>
                <ul className="space-y-1.5 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5" />
                    <span>Shared player database with your team</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5" />
                    <span>Collaborative shortlists and recruiting boards</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5" />
                    <span>Team communication and activity tracking</span>
                  </li>
                  {currentInvitation.role === 'admin' && (
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5" />
                      <span>Admin privileges to manage team settings</span>
                    </li>
                  )}
                </ul>
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={handleAccept} 
                  disabled={isLoading}
                  className="flex-1"
                >
                  {isLoading ? (
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Accept
                </Button>
                <Button 
                  onClick={handleDecline} 
                  disabled={isLoading}
                  variant="outline"
                  className="flex-1"
                >
                  <X className="h-4 w-4 mr-2" />
                  Decline
                </Button>
              </div>
            </>
          )}

          {invitations.length > 1 && (
            <p className="text-xs text-center text-muted-foreground">
              {invitations.indexOf(currentInvitation) + 1} of {invitations.length} invitations
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}